import { useEffect, useMemo, useRef } from "react";
import { buildFur, bodyEllipses, VIEW, PARTS } from "../lib/dogAnatomy";
import { setPurrLevel } from "../lib/purr";
import { IDS } from "../constants/testIds";

// Radius of the petting influence in SVG units. Matches the dog's local
// scale (viewBox is 1000x640). Hoisted outside the component so it isn't a
// React hook dependency.
const PET_R = 90;

/**
 * The sleeping dog illustration.
 *
 * - `windLevelRef`  : ref carrying wind intensity in [0, 1]. Small tremors
 *                    yield small values; large ones (>= 6.0) push it toward 1
 *                    and simultaneously set `awakeRef.current = true`.
 * - `awakeRef`     : ref carrying boolean; when true the dog's face swaps to
 *                    an alert pose (eyes open, ears pricked, mouth open).
 */
export default function SleepingDog({ windLevelRef, awakeRef, soundOn = true, onContentmentChange }) {
  const fur = useMemo(() => buildFur(1337), []);
  // Split into undercoat / topcoat for a two-tone etched look.
  const undercoat = useMemo(() => fur.filter((s) => s.layer <= 1), [fur]);
  const topcoat = useMemo(() => fur.filter((s) => s.layer >= 2), [fur]);

  const underPathRef = useRef(null);
  const topPathRef = useRef(null);
  const svgRef = useRef(null);
  const petHaloRef = useRef(null);
  const rafRef = useRef(null);
  const startedAt = useRef(performance.now());

  // Petting state -- x/y in SVG viewBox coords, strength 0..1 (smooth)
  const petRef = useRef({ x: 0, y: 0, strength: 0, hovering: false });

  // Contentment builds while petting, decays when not petting. Value in [0, 1].
  const contentmentRef = useRef(0);
  const heartRef = useRef(null);
  const heartOutlineRef = useRef(null);
  const purrLabelRef = useRef(null);
  const purrPctRef = useRef(null);
  const lastContentmentEmit = useRef(0);

  // Ear + eyes DOM refs so we can transition between sleep/wake without React state churn.
  const earSleepRef = useRef(null);
  const earAwakeRef = useRef(null);
  const eyeSleepRef = useRef(null);
  const eyeAwakeRef = useRef(null);
  const mouthAwakeRef = useRef(null);

  // Map a client-space mouse event to the SVG viewBox coordinates.
  const eventToSvg = (evt) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const x = ((evt.clientX - rect.left) / rect.width) * VIEW.w;
    const y = ((evt.clientY - rect.top) / rect.height) * VIEW.h;
    return { x, y };
  };

  const handlePointerMove = (evt) => {
    const { x, y } = eventToSvg(evt);
    petRef.current.x = x;
    petRef.current.y = y;
    petRef.current.hovering = true;
  };
  const handlePointerLeave = () => {
    petRef.current.hovering = false;
  };

  useEffect(() => {
    const buildD = (strands, time, wind, awake, pet) => {
      // A slower carrier + a faster ripple, both scaled by wind. `awake`
      // gently boosts amplitude so the fur bristles too.
      const carrier = 1.4; // rad/sec
      const ripple = 4.5;
      const windBoost = awake ? 1.5 : 1.0;
      const t = time * 0.001;
      const petActive = pet.strength > 0.01;
      const invR2 = 1 / (2 * PET_R * PET_R);
      let d = "";
      for (let i = 0; i < strands.length; i++) {
        const s = strands[i];
        // Wind angular deflection: soft when calm, dramatic when strong.
        const local =
          Math.sin(t * carrier + s.phase) * 0.65 +
          Math.sin(t * ripple + s.phase * 1.7) * 0.35;
        const deflect = local * wind * s.responsiveness * 1.05 * windBoost;
        const globalWindX = wind * 0.5 * windBoost;
        const globalWindY = -wind * 0.15 * windBoost;
        const bx = Math.cos(s.baseAngle);
        const by = Math.sin(s.baseAngle);
        const cos = Math.cos(deflect);
        const sin = Math.sin(deflect);
        let dirX = bx * cos - by * sin;
        let dirY = bx * sin + by * cos;
        dirX += globalWindX * s.responsiveness;
        dirY += globalWindY * s.responsiveness;
        const mag0 = Math.hypot(dirX, dirY) || 1;
        dirX /= mag0;
        dirY /= mag0;
        let x2 = s.x + dirX * s.length;
        let y2 = s.y + dirY * s.length;

        // Petting: strands close to the cursor bend AWAY from it (like a
        // finger dragging through fur) and also get a small time-based
        // ruffle so the coat visibly ripples.
        if (petActive) {
          const dx = s.x - pet.x;
          const dy = s.y - pet.y;
          const dist2 = dx * dx + dy * dy;
          const influence = Math.exp(-dist2 * invR2) * pet.strength;
          if (influence > 0.01) {
            const dist = Math.sqrt(dist2) || 1;
            const nx = dx / dist;
            const ny = dy / dist;
            // Push tip outward from cursor
            const push = influence * s.length * 1.6;
            x2 += nx * push;
            y2 += ny * push;
            // Fine ruffle jitter
            const rufflePhase = t * 6 + s.phase * 2.3;
            x2 += Math.sin(rufflePhase) * influence * 3.5;
            y2 += Math.cos(rufflePhase * 1.3) * influence * 3.5;
          }
        }

        d += `M${s.x.toFixed(1)} ${s.y.toFixed(1)}L${x2.toFixed(1)} ${y2.toFixed(1)}`;
      }
      return d;
    };

    const tick = (now) => {
      const wind = Math.max(0, Math.min(1.4, windLevelRef.current ?? 0.05));
      const awake = !!awakeRef.current;

      // Smoothly ease pet strength toward the hover target
      const pet = petRef.current;
      const target = pet.hovering ? 1 : 0;
      pet.strength += (target - pet.strength) * 0.14;
      if (pet.strength < 0.005) pet.strength = 0;

      // Contentment grows while petting (~15s to full), decays gently
      // otherwise. When awake, contentment drains faster.
      const dtSec = 1 / 60;
      const decayRate = awake ? 0.18 : 0.05;
      const buildRate = 1 / 15;
      const c = contentmentRef.current;
      const delta = pet.strength * buildRate - (1 - pet.strength) * decayRate;
      contentmentRef.current = Math.max(0, Math.min(1, c + delta * dtSec));

      // Emit contentment upward at ~5 fps so the parent doesn't re-render every frame
      if (onContentmentChange && now - lastContentmentEmit.current > 200) {
        lastContentmentEmit.current = now;
        onContentmentChange(contentmentRef.current);
      }

      // Drive the purr audio softly. Silence entirely when muted or awake.
      const purr = soundOn && !awake ? contentmentRef.current : 0;
      setPurrLevel(purr);

      if (underPathRef.current) {
        underPathRef.current.setAttribute("d", buildD(undercoat, now, wind * 0.6, awake, pet));
      }
      if (topPathRef.current) {
        topPathRef.current.setAttribute("d", buildD(topcoat, now, wind, awake, pet));
      }

      // Petting halo -- soft ring around the cursor while hovering the dog
      if (petHaloRef.current) {
        const halo = petHaloRef.current;
        if (pet.strength > 0.02) {
          halo.style.opacity = String(pet.strength * 0.55);
          halo.setAttribute("cx", pet.x.toFixed(1));
          halo.setAttribute("cy", pet.y.toFixed(1));
        } else {
          halo.style.opacity = "0";
        }
      }

      // Heart beat -- rate & scale scale with contentment. Two lub-dub pulses
      // per second when maxed, slow ~0.6/s at baseline.
      if (heartRef.current) {
        const cVal = contentmentRef.current;
        const bpm = 40 + cVal * 90; // 40 -> 130 bpm
        const beatFreq = bpm / 60;
        const phase = ((now * 0.001) * beatFreq) % 1;
        // Two quick lub-dub pulses within one beat
        const lubDub =
          Math.exp(-Math.pow((phase - 0.02) / 0.06, 2)) * 0.85 +
          Math.exp(-Math.pow((phase - 0.18) / 0.09, 2)) * 0.55;
        const baseVisible = cVal > 0.02;
        const scale = 1 + lubDub * (0.18 + cVal * 0.35);
        heartRef.current.setAttribute("transform", `translate(430 428) scale(${scale.toFixed(3)})`);
        heartRef.current.style.opacity = baseVisible ? String(Math.min(1, 0.3 + cVal * 1.1)) : "0";
        if (heartOutlineRef.current) {
          heartOutlineRef.current.style.opacity = baseVisible ? String(0.6 + cVal * 0.4) : "0";
        }
      }
      // Purring caption
      if (purrLabelRef.current) {
        const cVal = contentmentRef.current;
        purrLabelRef.current.style.opacity = cVal > 0.05 ? String(Math.min(1, cVal * 1.4)) : "0";
        if (purrPctRef.current) {
          purrPctRef.current.textContent = `${Math.round(cVal * 100)}%`;
        }
      }

      // Sleep/wake DOM swap
      if (earSleepRef.current && earAwakeRef.current) {
        earSleepRef.current.style.opacity = awake ? "0" : "1";
        earAwakeRef.current.style.opacity = awake ? "1" : "0";
      }
      if (eyeSleepRef.current && eyeAwakeRef.current) {
        eyeSleepRef.current.style.opacity = awake ? "0" : "1";
        eyeAwakeRef.current.style.opacity = awake ? "1" : "0";
      }
      if (mouthAwakeRef.current) {
        mouthAwakeRef.current.style.opacity = awake ? "1" : "0";
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      setPurrLevel(0);
    };
  }, [undercoat, topcoat, windLevelRef, awakeRef, soundOn, onContentmentChange]);

  return (
    <svg
      data-testid={IDS.dogSvg}
      ref={svgRef}
      viewBox={`0 0 ${VIEW.w} ${VIEW.h}`}
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto select-none breathing dog-pet"
      role="img"
      aria-label="A curled-up sleeping dog rendered in fine ink lines. Hover to pet him."
      onMouseMove={handlePointerMove}
      onMouseLeave={handlePointerLeave}
      onTouchMove={(e) => {
        if (e.touches[0]) handlePointerMove(e.touches[0]);
      }}
      onTouchEnd={handlePointerLeave}
    >
      {/* Ground shadow -- a soft ellipse under the dog to anchor him */}
      <ellipse
        cx="520"
        cy="555"
        rx="360"
        ry="16"
        fill="hsl(30 22% 55% / 0.35)"
      />
      <ellipse
        cx="520"
        cy="558"
        rx="300"
        ry="8"
        fill="hsl(30 30% 42% / 0.25)"
      />

      {/* Body silhouette: overlapping soft ellipses in a light ink, giving the
         curled form. The fur strands do most of the "shape reading" work. */}
      <g fill="none" stroke="hsl(24 22% 10%)" strokeWidth="1.1" opacity="0.88">
        {bodyEllipses.map((e) => (
          <ellipse
            key={`${e.cx}-${e.cy}-${e.rx}`}
            cx={e.cx}
            cy={e.cy}
            rx={e.rx}
            ry={e.ry}
            transform={`rotate(${(e.rot * 180) / Math.PI} ${e.cx} ${e.cy})`}
            fill="hsl(41 44% 88%)"
          />
        ))}
      </g>

      {/* Tail (curled bushy) -- drawn as a path so it doesn't look ellipse-y */}
      <path
        d="M760 320 Q 870 260 900 340 Q 910 410 830 405 Q 785 400 760 380"
        fill="hsl(41 44% 88%)"
        stroke="hsl(24 22% 10%)"
        strokeWidth="1.1"
        opacity="0.88"
      />

      {/* Undercoat -- shorter, thinner fur */}
      <path
        ref={underPathRef}
        stroke="hsl(28 18% 26%)"
        strokeWidth="0.55"
        strokeLinecap="round"
        opacity="0.55"
        fill="none"
      />
      {/* Topcoat -- longer, darker fur */}
      <path
        ref={topPathRef}
        stroke="hsl(24 22% 10%)"
        strokeWidth="0.75"
        strokeLinecap="round"
        opacity="0.9"
        fill="none"
      />

      {/* Ear (sleep) -- floppy, drooping */}
      <g ref={earSleepRef} style={{ transition: "opacity 220ms ease" }}>
        <ellipse
          cx={PARTS.ear.cx}
          cy={PARTS.ear.cy}
          rx={PARTS.ear.rx}
          ry={PARTS.ear.ry}
          transform={`rotate(${(PARTS.ear.rot * 180) / Math.PI} ${PARTS.ear.cx} ${PARTS.ear.cy})`}
          fill="hsl(38 32% 78%)"
          stroke="hsl(24 22% 10%)"
          strokeWidth="1.1"
        />
        {/* inner ear line */}
        <path
          d={`M ${PARTS.ear.cx - 40} ${PARTS.ear.cy - 4} Q ${PARTS.ear.cx - 4} ${PARTS.ear.cy + 6} ${PARTS.ear.cx + 34} ${PARTS.ear.cy + 4}`}
          fill="none"
          stroke="hsl(24 22% 22%)"
          strokeWidth="0.7"
          opacity="0.7"
        />
      </g>

      {/* Ear (awake) -- pricked up, more vertical */}
      <g ref={earAwakeRef} style={{ opacity: 0, transition: "opacity 220ms ease" }}>
        <path
          d="M 240 320 Q 220 220 275 210 Q 305 240 300 320 Z"
          fill="hsl(38 32% 78%)"
          stroke="hsl(24 22% 10%)"
          strokeWidth="1.2"
        />
        <path
          d="M 253 305 Q 258 260 285 250"
          fill="none"
          stroke="hsl(24 22% 22%)"
          strokeWidth="0.8"
          opacity="0.7"
        />
      </g>

      {/* Face group */}
      <g>
        {/* Snout tip + nose */}
        <ellipse
          cx="112"
          cy="418"
          rx="14"
          ry="10"
          fill="hsl(24 22% 12%)"
          stroke="hsl(24 22% 6%)"
          strokeWidth="0.6"
        />
        {/* Mouth line (sleep) */}
        <path
          d="M 120 432 Q 155 448 190 442"
          fill="none"
          stroke="hsl(24 22% 12%)"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
        {/* Little tongue-tip peeking */}
        <path
          d="M 160 447 q 4 4 -3 6"
          fill="hsl(8 62% 45%)"
          stroke="hsl(24 22% 12%)"
          strokeWidth="0.5"
          opacity="0.85"
        />

        {/* Whiskers */}
        <g stroke="hsl(24 22% 15%)" strokeWidth="0.4" fill="none" opacity="0.6">
          <path d="M 138 424 L 90 418" />
          <path d="M 140 432 L 88 434" />
          <path d="M 142 440 L 92 446" />
        </g>

        {/* Closed eye (sleep) */}
        <g ref={eyeSleepRef} style={{ transition: "opacity 180ms ease" }}>
          <path
            d="M 200 395 q 18 -8 34 0"
            fill="none"
            stroke="hsl(24 22% 10%)"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          <path
            d="M 205 390 q 4 -3 7 -1"
            fill="none"
            stroke="hsl(24 22% 10%)"
            strokeWidth="0.6"
            opacity="0.7"
          />
          <path
            d="M 226 389 q 3 -3 7 -1"
            fill="none"
            stroke="hsl(24 22% 10%)"
            strokeWidth="0.6"
            opacity="0.7"
          />
        </g>

        {/* Open eye (awake) */}
        <g ref={eyeAwakeRef} style={{ opacity: 0, transition: "opacity 180ms ease" }}>
          <path
            d="M 200 395 q 18 -12 36 0 q -18 8 -36 0 Z"
            fill="hsl(41 44% 92%)"
            stroke="hsl(24 22% 10%)"
            strokeWidth="1.3"
          />
          <circle cx="220" cy="394" r="4.5" fill="hsl(24 22% 10%)" />
          <circle cx="221.5" cy="392.5" r="1.2" fill="hsl(41 44% 92%)" />
        </g>

        {/* Alert open mouth (awake) */}
        <g ref={mouthAwakeRef} style={{ opacity: 0, transition: "opacity 180ms ease" }}>
          <path
            d="M 122 434 Q 155 460 195 445 Q 175 452 130 442 Z"
            fill="hsl(8 62% 22%)"
            stroke="hsl(24 22% 8%)"
            strokeWidth="1"
          />
          <path
            d="M 130 442 q 8 6 20 8"
            fill="none"
            stroke="hsl(8 62% 55%)"
            strokeWidth="0.8"
            opacity="0.9"
          />
        </g>
      </g>

      {/* Purring heart -- appears on his chest as contentment builds, beats
         faster and grows brighter as he settles in. Positioned via ref
         updates for smooth per-frame motion. */}
      <g pointerEvents="none">
        {/* Ghost outline behind the beating heart (double-ink hand-drawn feel) */}
        <path
          ref={heartOutlineRef}
          d="M 0 4 C -14 -10 -26 6 0 22 C 26 6 14 -10 0 4 Z"
          transform="translate(430 428) scale(1)"
          fill="none"
          stroke="hsl(14 66% 32%)"
          strokeWidth="0.8"
          opacity="0"
          style={{ mixBlendMode: "multiply" }}
        />
        <path
          ref={heartRef}
          d="M 0 4 C -12 -8 -22 4 0 18 C 22 4 12 -8 0 4 Z"
          transform="translate(430 428) scale(1)"
          fill="hsl(14 66% 32%)"
          stroke="hsl(24 22% 8%)"
          strokeWidth="0.8"
          opacity="0"
          style={{ transformOrigin: "0 0" }}
        />
      </g>

      {/* Purring caption near the muzzle */}
      <g ref={purrLabelRef} pointerEvents="none" opacity="0" style={{ transition: "opacity 200ms ease" }} data-testid={IDS.purrLabel}>
        <text
          x="60"
          y="380"
          className="mono"
          fontSize="10"
          fill="hsl(24 22% 22%)"
          letterSpacing="2"
        >
          PURRING &middot; <tspan ref={purrPctRef} fill="hsl(14 66% 32%)" fontWeight="600">0%</tspan>
        </text>
        <path
          d="M 60 388 q 6 -3 12 0 t 12 0 t 12 0"
          fill="none"
          stroke="hsl(14 66% 32%)"
          strokeWidth="0.7"
          opacity="0.7"
        />
      </g>

      {/* Petting halo -- soft rings around the cursor while hovering the dog */}
      <g pointerEvents="none">
        <circle
          ref={petHaloRef}
          cx="-100"
          cy="-100"
          r="34"
          fill="none"
          stroke="hsl(24 22% 10%)"
          strokeWidth="0.6"
          strokeDasharray="2 3"
          opacity="0"
          style={{ transition: "opacity 200ms ease" }}
        />
      </g>
    </svg>
  );
}
